import { useMemo } from "react";
import { useGetBusiness } from "@/business/query";
import { useGetSpiffApprovals } from "@/spiff-approvals/query";
import { useGetPendingSpiffs, useGetDismissedSpiffs } from "@/spiffs/query";
import { useGetBusinessUsers } from "@/user/query";
import { enrichPayrollPeriods } from "./utils/enrich-payroll-period";
import { groupSpiffsByPayrollPeriod } from "./utils/group-spiffs-by-period";

/**
 * Get enriched payroll periods with user data
 * Follows the same pattern as useGetEnrichedSpiffApprovals
 */
export const useGetEnrichedPayrollPeriods = (businessId?: string | null) => {
  const { data: business, isLoading: isLoadingBusiness } =
    useGetBusiness(businessId);
  const { data: pendingSpiffs = {}, isLoading: isLoadingPendingSpiffs } =
    useGetPendingSpiffs(businessId);
  const { data: dismissedSpiffs = {}, isLoading: isLoadingDismissedSpiffs } =
    useGetDismissedSpiffs(businessId);
  const { data: spiffApprovals = [], isLoading: isLoadingApprovals } =
    useGetSpiffApprovals(businessId);
  const { data: businessUsers = [], isLoading: isLoadingUsers } =
    useGetBusinessUsers(businessId);

  const template = business?.current_pay_period_template;

  const periodsWithSpiffs = useMemo(() => {
    if (!business || !template) {
      return [];
    }

    const { periodsWithSpiffs } = groupSpiffsByPayrollPeriod(
      pendingSpiffs,
      dismissedSpiffs,
      spiffApprovals,
      template,
    );

    return enrichPayrollPeriods(periodsWithSpiffs, businessUsers);
  }, [
    business,
    pendingSpiffs,
    dismissedSpiffs,
    spiffApprovals,
    template,
    businessUsers,
  ]);

  const isLoading =
    isLoadingBusiness ||
    isLoadingPendingSpiffs ||
    isLoadingDismissedSpiffs ||
    isLoadingApprovals ||
    isLoadingUsers;

  return {
    periodsWithSpiffs,
    isLoading,
  };
};
